import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import React, {
  useCallback,
  useState,
  useContext,
  useRef,
  RefObject,
  useMemo,
  useEffect
} from 'react'
import classnames from 'classnames'
import { useRouter } from 'next/router'
import { useOnClickOutside } from '../../../hooks/misc/use-on-click-outside'
import { UserAvatar } from '../../../components/sidebar/user-avatar'
import { trigger } from '../../../lib/events'
import { BannerCountdown } from '../../../components/sidebar/banner-countdown/banner-countdown'
import { UseCampaignCountdown } from '../../../hooks/campaign/use-campaing-countdown/use-campaign-countdown'
import { AnnouncementBanner } from '../../../components/sidebar/announcement-banner'
import { UseAnnouncement } from '../../../hooks/announcements/use-announcement'
import { ContextShell } from '../../context'
import { DropMenu } from '../../../components/drop-menu'
import { ButtonIcon } from '../../../components/button-icon'
import { Icon } from '../../../components/icon'
import { useDataManageGroup } from '../../../hooks/group/use-data-manage-group'
import { useStoreModals } from '../../../hooks/modals/use-store-modals'
import {
  REACTIVATE_SUBSCRIPTION_BANNER_ID,
  ReactivateSubscriptionBanner
} from '../../../components/sidebar/reactivate-subscription-banner'
import { replacePlaceholders } from '../../../lib/misc/replace-placeholders'
import {
  REACTIVATE_SUBSCRIPTION_KEY,
  UseReactivateSubscriptionPaywall
} from '../../../hooks/paywall'
import styles from './nav-user.module.scss'
import { safeJsonParse } from '../../../utils/safe-json-parse'

interface NavUserProps {
  className?: string
  small?: boolean
  reactivateSubscription?: UseReactivateSubscriptionPaywall
  bannerCountdown?: UseCampaignCountdown
  announcement?: UseAnnouncement
  showDaysInCountdown?: boolean
}

interface UserPlanProps {
  isPro?: boolean
  isPremium?: boolean
  isGroupSubscription?: boolean
}

const UserPlan: React.FC<UserPlanProps> = ({
  isPro,
  isPremium,
  isGroupSubscription
}) => {
  const { i18n } = useLingui()
  return (
    <p
      id="account_status"
      className={classnames(styles.plan, {
        [styles.isStarter]: !isPro && !isPremium,
        [styles.isPremium]: isPremium && !isPro,
        [styles.isPro]: isPro,
        [styles.isGroup]: isGroupSubscription
      })}
    >
      {isPro
        ? i18n._(t`producer_plan_title`)
        : isPremium
        ? i18n._(t`pricing_plan_musician`)
        : i18n._(t`pricing_starter_title`)}
      {isGroupSubscription && ` ${i18n._(t`Group`)}`}
    </p>
  )
}

export const NavUser: React.FC<NavUserProps> = ({
  className,
  small,
  bannerCountdown,
  announcement,
  reactivateSubscription,
  showDaysInCountdown = true
}) => {
  const ref: RefObject<any> = useRef(null)
  const { i18n } = useLingui()
  const { pathname } = useRouter()
  const {
    user,
    event: { sendEventPostHog }
  } = useContext(ContextShell)
  const {
    modalReferralLink: { onOpen: onOpenReferralLinkModal },
    modalShortcutsMenu: { onOpen: onOpenModalShortcutsMenu }
  } = useStoreModals()
  const { group, isEnabledManagement } = useDataManageGroup()
  const [showNav, setShowNav] = useState(false)

  useOnClickOutside(ref, () => setShowNav(false))

  const active = useMemo(
    () =>
      showNav ||
      ['/settings', '/notifications/preferences', '/notifications'].includes(
        pathname
      ),
    [pathname, showNav]
  )

  const onOpenNav = useCallback(() => setShowNav(!showNav), [showNav])

  const onGoSettings = useCallback(() => {
    setShowNav(false)
    window.location.href = '/settings'
  }, [])

  const onGoNotifications = useCallback(() => {
    setShowNav(false)
    window.location.href = '/notifications/preferences/'
  }, [])

  const onGoPricing = useCallback(() => {
    setShowNav(false)
    window.location.href = '/billing/pricing/?source=profile-settingsCTA'
  }, [])

  const onGoSupport = useCallback(() => {
    setShowNav(false)
    window.open('/support', '_blank', 'noopener')
  }, [])

  const onGoWhatsNew = useCallback(() => {
    setShowNav(false)
    window.open(
      'https://moises.ai/blog/moises-news/improvements-latest-releases/',
      '_blank',
      'noopener'
    )
  }, [])

  const onGoLogOut = useCallback(() => {
    setShowNav(false)
    trigger('purge:tasks')
    localStorage.removeItem('moises:userSortingPreference')
    localStorage.removeItem('moises:userFilterPreference')
    localStorage.removeItem('moises:userMasteringFilterPreference')
    localStorage.removeItem('moises:userMasteringSortingPreference')
    localStorage.removeItem('moises:userVoiceSortingPreference')
    localStorage.removeItem('moises:userVoiceFilterPreference')
    localStorage.removeItem('moises:is-first-ai-studio-session')
    localStorage.removeItem(REACTIVATE_SUBSCRIPTION_KEY)
    localStorage.removeItem(REACTIVATE_SUBSCRIPTION_BANNER_ID)
    window.location.href = '/logout'
  }, [])

  const onGoKeyboardShortcuts = useCallback(() => {
    setShowNav(false)
    onOpenModalShortcutsMenu()
  }, [onOpenModalShortcutsMenu])

  const onGoRerralLink = useCallback(() => {
    setShowNav(false)
    onOpenReferralLinkModal()
  }, [onOpenReferralLinkModal])

  const onGoManageGroup = useCallback(() => {
    setShowNav(false)
    window.location.href = '/group/manage'
  }, [])

  const isPayingUser = useMemo(
    () => user?.subscription?.isPro || user?.subscription?.isPremium,
    [user]
  )

  const isProUser = user?.subscription?.isPro

  const allowReferAFriend = useMemo(
    () =>
      isPayingUser &&
      user?.subscription?.details?.providerName !== 'trial' &&
      user?.subscription?.details?.autoRenew,
    [isPayingUser, user]
  )

  const bannerToRender = useMemo(() => {
    if (small) return null
    if (reactivateSubscription?.show) return 'reactivate-subscription'
    if (bannerCountdown?.show) return 'promo'
    if (announcement?.show) return 'feature'

    return null
  }, [
    small,
    reactivateSubscription?.show,
    bannerCountdown?.show,
    announcement?.show
  ])

  const resubscribedCompleted = useMemo((): boolean => {
    const stored = safeJsonParse(
      localStorage.getItem(REACTIVATE_SUBSCRIPTION_KEY)
    )

    if (!stored || stored?.userId !== user?.id) {
      return false
    }

    const prev = stored.autoRenew
    const now = user?.subscription?.details?.autoRenew

    return prev === false && now === true
  }, [user?.id, user?.subscription?.details?.autoRenew])

  useEffect(() => {
    if (!resubscribedCompleted) return

    sendEventPostHog({
      name: 'purchase_flow:subscription_reactivate',
      event_category: 'growth',
      customAttributes: {
        subscription_plan: user?.subscription?.plan,
        subscription_plan_cycle: user?.subscription?.details?.planCycle,
        source: 'reactivate-subscription-5-active-projects'
      }
    })

    localStorage.removeItem(REACTIVATE_SUBSCRIPTION_KEY)
    localStorage.removeItem(REACTIVATE_SUBSCRIPTION_BANNER_ID)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    resubscribedCompleted,
    user?.subscription?.plan,
    user?.subscription?.details?.planCycle
  ])

  return (
    <div className={classnames(className, styles.container)}>
      {bannerToRender === 'reactivate-subscription' && (
        <ReactivateSubscriptionBanner
          {...(reactivateSubscription as UseReactivateSubscriptionPaywall)}
          show={!small && !!reactivateSubscription?.show}
          image="/assets_shell/assets/images/bg-banner-warning.png"
          title={i18n._(t`your_subscription_is_expiring_soon`)}
          description={replacePlaceholders(
            i18n._(t`paywall_5_file_expire_long_description`),
            { total: 5 }
          )}
          buttonText={i18n._(t`button_keep_subscription`)}
        />
      )}
      {bannerToRender === 'promo' && (
        <BannerCountdown
          {...(bannerCountdown as UseCampaignCountdown)}
          show={!small && !!bannerCountdown?.show}
          showDaysInCountdown={showDaysInCountdown}
        />
      )}
      {bannerToRender === 'feature' && (
        <AnnouncementBanner
          {...(announcement as UseAnnouncement)}
          show={!small && !!announcement?.show}
        />
      )}
      <button
        id="user_info_button"
        type="button"
        onClick={small ? onGoSettings : onOpenNav}
        className={classnames(styles.button, {
          [styles.active]: active,
          [styles.small]: small
        })}
      >
        <UserAvatar user={user} />
        {!small && (
          <div className={styles.user}>
            <p className={styles.title}>
              {(user && user.name) || (user && user.email)}
            </p>
            <UserPlan
              isPro={user?.subscription?.isPro}
              isPremium={user?.subscription?.isPremium}
              isGroupSubscription={!!group}
            />
          </div>
        )}
      </button>
      <div ref={ref}>
        <DropMenu
          className={classnames(styles.drop, {
            [styles.show]: showNav && !small
          })}
        >
          <ButtonIcon
            id="user_info_get_premium"
            light
            icon={isProUser ? <Icon name="cards" /> : <Icon name="star" />}
            title={
              isProUser
                ? i18n._(t`plans_left_side_bar_menu`)
                : i18n._(t`unlock_all_features`)
            }
            className={classnames({
              [styles.buttonPremium]: !isProUser
            })}
            onClick={onGoPricing}
          />

          <ButtonIcon
            id="user_info_account_settings"
            light
            icon={<Icon name="settings" />}
            title={i18n._(t`header.nav.user.settings`)}
            onClick={onGoSettings}
          />

          {isEnabledManagement && (
            <ButtonIcon
              light
              icon={<Icon name="user-group-3" />}
              title={i18n._(t`manage_group_title`)}
              onClick={onGoManageGroup}
              id="manage_group_button"
            />
          )}

          <ButtonIcon
            id="user_info_notification"
            light
            icon={<Icon name="notification" />}
            title={i18n._(t`notification_settings_title`)}
            onClick={onGoNotifications}
          />

          {allowReferAFriend && (
            <ButtonIcon
              id="refer_a_friend"
              light
              icon={<Icon name="invite-user" />}
              title={t`refer_friend_title`}
              onClick={onGoRerralLink}
            />
          )}

          <ButtonIcon
            id="user_info_keyboard_shortcuts"
            light
            icon={<Icon name="keyboard" />}
            title={i18n._(t`keyboard_shortcuts`)}
            onClick={onGoKeyboardShortcuts}
          />

          <ButtonIcon
            id="user_info_support"
            light
            icon={<Icon name="support" />}
            title={i18n._(t`label.support`)}
            onClick={onGoSupport}
          />

          <ButtonIcon
            id="user_info_whatsnew"
            light
            icon={<Icon name="gift-box" />}
            title={`${i18n._(t`modal.mobile.title`)}?`}
            onClick={onGoWhatsNew}
          />

          <ButtonIcon
            id="user_info_sign_out"
            light
            icon={<Icon name="logout" />}
            title={i18n._(t`header.nav.user.logout`)}
            onClick={onGoLogOut}
          />
        </DropMenu>
      </div>
    </div>
  )
}
